import axios from 'axios';
import { attach } from 'perimeterx-axios-interceptor';
import getMetaTagValue from '../../../../lib/getMetaTagValue';
import {
    INTERCEPTOR_EVENTS,
    INTERCEPTOR_MESSAGES,
    LOG_CODE,
    IGNORE_PATTERN
} from './constants';

const statsApi = getMetaTagValue('browser_stats_api_v1');

/**
 * Feature detects es6 browser via spread operator support, in order to set spoof flag.
 * @return {Boolean}
 */
const detectES6 = () => {
    try {
        eval('const o={},x={...o};new Map()'); // eslint-disable-line no-eval
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Create a stat metric entry
 * @param {String} key Unique metric key
 * @returns {Object}
 */
const metric = (key) => ({
    key: ['axios.interceptor.perimeterx', key].join('.'),
    type: 'count',
    value: 1
});

/**
 * Creates an interception event details
 * @param {Request} request - The request that has been intercepted
 * @param {String} type - the interception type
 * @param {String} message - The message
 * @param {Error} error - The error if any that was intercepted by a failure
 * @param {String} level - The log level to report with
 * @return {Object}
 */
const details = ({ request, type, message = INTERCEPTOR_MESSAGES[type], error, level = 'info' }) => ({
    log: {
        type,
        level,
        message,
        url: request.url,
        code: LOG_CODE,
        ...(error && {
            message: error.message,
            stack: error.stack
        })
    },
    stat: metric(type)
});

/**
 * Assign metadata to the request payload
 * @param {Object} data - The data to enrich
 * @returns {Object}
 */
const enrich = (data) => {
    const meta = {
        url: window.location.href,
        referrer: document.referrer,
        readyState: document.readyState,
        facility: getMetaTagValue('fiverrmeta:facility') || 'web',
        creation: getMetaTagValue('fiverrmeta:creation'),
        locale: getMetaTagValue('fiverrmeta:locale'),
        pagename: getMetaTagValue('fiverrmeta:pagename'),
        is_known_crawler: getMetaTagValue('fiverrmeta:is_known_crawler') === 'true',
        suspected_spoof: !detectES6() && getMetaTagValue('es6_browser') === 'true'
    };

    try {
        meta.pxid = window.sessionStorage.getItem('pxsid');
        meta.age = Math.round(performance.now() / 1000);
    } catch { /* ignores */ }

    return Object.assign(data, { meta });
};

/**
 * Creates a clean stack
 * @returns {Object}
 */
const cleanStack = () => ({
    logs: [],
    stats: []
});

let timer;
let reportStack = cleanStack();

/**
 * De-bounced logs and stats repotring
 * @param {Object} options.log  Log object
 * @param {Object} options.stat Stats object
 */
const report = ({ log, stat }) => {
    clearTimeout(timer);

    reportStack.logs.push(log);
    reportStack.stats.push(stat);

    enrich(reportStack);

    timer = setTimeout(
        () => axios.post(statsApi, reportStack)
            .finally(() => {
                reportStack = cleanStack();
            }),
        500
    );
};

/**
 * Attach Axios interceptor
 * @param {Axios} axios - The axios instance to attach the interceptor to
 */
const register = (axios) => {
    if (!axios || !statsApi) { return; }

    // Attach PerimeterX interceptor
    attach(axios, {

        /**
         * Should this route being blocked result in the user view the exonerate GUI
         * @param {String} options.path
         * @returns {Boolean}
         */
        filter: ({ path }) => !IGNORE_PATTERN.test(path.replace(/^https:\/\/\w{2,4}\.fiverr\.com/, '')),

        /**
         * Report blocked request interception
         * @param {Request} request Original request
         * @returns {void}
         */
        onintercept: (request) => report(
            details({ request, type: INTERCEPTOR_EVENTS.INTERCEPT }),
        ),

        /**
         * Report ignored blocked request interception
         * @param {Request} request Original request
         */
        onignore: (request) => report(
            details({ request, type: INTERCEPTOR_EVENTS.IGNORE }),
        ),

        /**
         * Report successful exoneration process
         * @param {Request} request Original request
         */
        onsuccess: (request) => report(
            details({ request, type: INTERCEPTOR_EVENTS.SUCCESS }),
        ),

        /**
         * Report unsuccessful exoneration process
         * @param {Request} request Original request
         * @param {Error} error Error thrown by axios
         */
        onfailure: (request, error) => report(
            details({ request, type: INTERCEPTOR_EVENTS.FAILURE, error }),
        ),

        /**
         * Report failure in the process
         * @param {Error} error Error thrown by exoneration process
         */
        onerror: (error) => setTimeout(() => {
            if (error.code === 'UNSUPPORTED_BROWSER') {
                return;
            }

            error.code = 'AXIOS_INTERCEPTOR_ERROR';
            throw error;
        }),

        modalConfig: { timeout: 6000 }
    });
};

export default register;
